import { ErrorHandler, inject, Service } from '@angular/core';
import { ToastService } from './toast';

/**
 * A failed dynamic `import()` of a lazy chunk. Happens when the loaded
 * `index.html` references a content-hashed chunk the server no longer has —
 * i.e. the client is running a stale build after a deploy. The browser surfaces
 * this with one of several messages depending on engine; match them all.
 */
function isChunkLoadError(error: unknown): boolean {
  const msg = (error instanceof Error ? error.message : String(error ?? '')).toLowerCase();
  return (
    msg.includes('dynamically imported module') ||
    msg.includes('error loading dynamically') ||
    msg.includes('importing a module script failed') ||
    msg.includes('chunkloaderror') ||
    /loading chunk \d+ failed/.test(msg) ||
    (msg.includes('failed to fetch') && msg.includes('module'))
  );
}

/**
 * Global error handler. Catches unhandled Angular errors and shows a toast.
 * Registered via `{ provide: ErrorHandler, useClass: GlobalErrorHandler }` in each app.
 *
 * Stale-build recovery: a failed lazy-chunk import means the client is running
 * an `index.html` whose hashed chunks the server no longer serves (a deploy
 * landed mid-session). We reload **once** to pull the current shell + chunks —
 * without this, lazy routes (e.g. `/oip` after mock login) dead-end on a chunk
 * that 404s as `text/html`. A short-lived sessionStorage stamp prevents a
 * reload loop if the reload itself can't recover.
 */
@Service({ autoProvided: false })
export class GlobalErrorHandler implements ErrorHandler {
  readonly #toast = inject(ToastService);

  /** Cooldown between recovery reloads (ms) — guards against reload loops. */
  static readonly RELOAD_COOLDOWN_MS = 10_000;
  static readonly RELOAD_KEY = 'vantage_chunk_reload_at';

  handleError(error: unknown): void {
    if (isChunkLoadError(error)) {
      console.warn('[Vantage] stale lazy chunk — attempting reload to recover', error);
      if (this.#reloadOnceForStaleChunk()) return;
      // Already reloaded recently and still failing — don't loop; ask the user.
      this.#toast.show({ type: 'error', message: 'A new version is available — please refresh the page.' });
      return;
    }
    const message = error instanceof Error ? error.message : String(error);
    console.error('[Vantage]', error);
    this.#toast.show({ type: 'error', message: 'An unexpected error occurred.', detail: message });
  }

  /** Reload once within the cooldown window; returns true if a reload was triggered. */
  #reloadOnceForStaleChunk(): boolean {
    let last = 0;
    try {
      last = Number(globalThis.sessionStorage?.getItem(GlobalErrorHandler.RELOAD_KEY) ?? 0);
    } catch { /* sessionStorage unavailable — proceed to reload */ }
    const now = this.now();
    if (now - last <= GlobalErrorHandler.RELOAD_COOLDOWN_MS) return false;
    try {
      globalThis.sessionStorage?.setItem(GlobalErrorHandler.RELOAD_KEY, String(now));
    } catch { /* ignore */ }
    this.reloadPage();
    return true;
  }

  /** Seam for tests. */
  protected now(): number {
    return Date.now();
  }

  /** Seam for tests — full reload re-fetches the current index.html + chunks. */
  protected reloadPage(): void {
    globalThis.location?.reload();
  }
}
